import type React from 'react'

export type MyNaviTypographyVariant =
  | 'heading' // ページタイトル（16px, bold, #058bbe）
  | 'subheading' // セクション見出し（14px, bold, #333333）
  | 'body' // 本文（14px, regular, #686868）
  | 'label' // ナビゲーションラベル等（14px, regular, #686868）
  | 'caption' // 補足テキスト（12px, regular, #686868）
  | 'caption-white' // 白色補足テキスト（12px, regular, white）
  | 'points' // ポイント数値（36px, bold, white）
  | 'points-label' // 「獲得ポイント」等（14px, regular, white）
  | 'back-label' // 「戻る」リンクテキスト（14px, black）
  | 'search-hint' // 「オープンキャンパスを探す！」（16px, regular, #686868）
  | 'dialog-title' // ダイアログタイトル（18px, bold, #333333）
  | 'dialog-body' // ダイアログ本文（15px, regular, #333333）
  | 'college-name' // 大学名表示（20px, regular, #686868）
  | 'large-title' // 大見出し・ダイアログヒーロー（24px, bold, #333333）
  | 'tiny' // 小補足テキスト（10px, regular, #686868）
  | 'micro' // 極小テキスト（8px, regular, #686868）

export type MyNaviTypographyProps = {
  variant: MyNaviTypographyVariant
  children: React.ReactNode
  className?: string
  /** インラインスタイルの上書き（variant のデフォルトスタイルに追記・上書き） */
  style?: React.CSSProperties
  /** HTMLタグのオーバーライド（デフォルトは variant ごとに決定） */
  as?: 'p' | 'span' | 'h1' | 'h2' | 'h3' | 'div' | 'button'
}

const styleMap: Record<MyNaviTypographyVariant, React.CSSProperties> = {
  heading: { fontSize: '16px', fontWeight: 700, color: '#058bbe', fontFamily: "'BIZ UDPGothic', sans-serif" },
  subheading: { fontSize: '14px', fontWeight: 700, color: '#333333', fontFamily: "'BIZ UDPGothic', sans-serif" },
  body: { fontSize: '14px', fontWeight: 400, color: '#686868', fontFamily: "'BIZ UDPGothic', sans-serif" },
  label: { fontSize: '14px', fontWeight: 400, color: '#686868', fontFamily: "'BIZ UDPGothic', sans-serif" },
  caption: { fontSize: '12px', fontWeight: 400, color: '#686868', fontFamily: "'BIZ UDPGothic', sans-serif" },
  'caption-white': { fontSize: '12px', fontWeight: 400, color: '#ffffff', fontFamily: "'BIZ UDPGothic', sans-serif" },
  points: {
    fontSize: '36px',
    fontWeight: 700,
    color: '#fbfbfb',
    lineHeight: '1',
    fontFamily: "'BIZ UDPGothic', sans-serif",
  },
  'points-label': { fontSize: '14px', fontWeight: 400, color: '#fbfbfb', fontFamily: "'BIZ UDPGothic', sans-serif" },
  'back-label': { fontSize: '14px', fontWeight: 400, color: '#000000', fontFamily: "'BIZ UDPGothic', sans-serif" },
  'search-hint': { fontSize: '16px', fontWeight: 400, color: '#686868', fontFamily: "'BIZ UDPGothic', sans-serif" },
  'dialog-title': { fontSize: '18px', fontWeight: 700, color: '#333333', fontFamily: "'BIZ UDPGothic', sans-serif" },
  'dialog-body': { fontSize: '15px', fontWeight: 400, color: '#333333', fontFamily: "'BIZ UDPGothic', sans-serif" },
  'college-name': { fontSize: '20px', fontWeight: 400, color: '#686868', fontFamily: "'BIZ UDPGothic', sans-serif" },
  'large-title': { fontSize: '24px', fontWeight: 700, color: '#333333', fontFamily: "'BIZ UDPGothic', sans-serif" },
  tiny: { fontSize: '10px', fontWeight: 400, color: '#686868', fontFamily: "'BIZ UDPGothic', sans-serif" },
  micro: { fontSize: '8px', fontWeight: 400, color: '#686868', fontFamily: "'BIZ UDPGothic', sans-serif" },
}

const defaultTagMap: Record<MyNaviTypographyVariant, MyNaviTypographyProps['as']> = {
  heading: 'p',
  subheading: 'p',
  body: 'p',
  label: 'span',
  caption: 'span',
  'caption-white': 'span',
  points: 'span',
  'points-label': 'p',
  'back-label': 'span',
  'search-hint': 'p',
  'dialog-title': 'h2',
  'dialog-body': 'p',
  'college-name': 'p',
  'large-title': 'h1',
  tiny: 'span',
  micro: 'span',
}

export const MyNaviTypography = ({ variant, children, className, style, as }: MyNaviTypographyProps) => {
  const Tag = as ?? defaultTagMap[variant] ?? 'span'
  return (
    <Tag style={{ ...styleMap[variant], ...style }} className={className}>
      {children}
    </Tag>
  )
}
